<template>
  <hb-section class="campaign-card">
    <div class="campaign-card__head">
      <div class="campaign-card__identity">
        <div class="campaign-card__name">{{ campaignName(participation) }}</div>
        <div class="campaign-card__entity">{{ entityLine(participation) }}</div>
      </div>
      <b-badge v-if="endingSoon" variant="light-warning">
        {{ $t('influencer_panel.ends_in_days', { days: participation.days_to_end }) }}
      </b-badge>
      <hb-status-badge v-else :status="participation.status" domain="participation" />
    </div>

    <div class="campaign-card__earned">
      <span class="campaign-card__earned-label">{{ $t('influencer_panel.you_earned') }}</span>
      <hb-money :cents="metric('commission_cents')" size="lg" tone="brand" />
    </div>

    <div class="campaign-card__metrics">
      <div class="campaign-card__metric">
        <div class="campaign-card__metric-label">{{ $t('influencer_panel.commission') }}</div>
        <div class="campaign-card__metric-value">{{ commissionLabel(participation) }}</div>
      </div>
      <div class="campaign-card__metric">
        <div class="campaign-card__metric-label">{{ $t('influencer_panel.sales') }}</div>
        <div class="campaign-card__metric-value">{{ metric('conversions') }}</div>
      </div>
      <div class="campaign-card__metric">
        <div class="campaign-card__metric-label">{{ $t('influencer_panel.clicks') }}</div>
        <div class="campaign-card__metric-value">{{ metric('clicks').toLocaleString('pt-BR') }}</div>
      </div>
      <div class="campaign-card__metric">
        <div class="campaign-card__metric-label">{{ $t('influencer_panel.conversion_rate') }}</div>
        <div class="campaign-card__metric-value">{{ conversionRate }}</div>
      </div>
    </div>

    <!-- Campanha sem data de fim nao tem barra: nao ha periodo para percorrer. -->
    <div v-if="participation.period_elapsed_pct !== null" class="campaign-card__period">
      <div class="campaign-card__period-row">
        <span>{{ periodRange }}</span>
        <span>{{ $t('influencer_panel.period_elapsed', { percent: participation.period_elapsed_pct }) }}</span>
      </div>
      <div class="campaign-card__bar">
        <div class="campaign-card__bar-fill" :style="{ width: `${participation.period_elapsed_pct}%` }" />
      </div>
    </div>

    <!-- O cupom e a metade nao-rastreavel do trabalho do influencer: fica em faixa
         propria, copiavel, e nao escondido no meio dos botoes de acao. -->
    <div v-if="participation.coupon_code" class="campaign-card__coupon">
      <feather-icon icon="TagIcon" size="14" />
      <code class="campaign-card__coupon-code">{{ participation.coupon_code }}</code>
      <span v-if="couponDiscount" class="campaign-card__coupon-discount">
        {{ $t('influencer_panel.coupon_discount', { discount: couponDiscount }) }}
      </span>
      <hb-copy-button :text="participation.coupon_code" :allow-share="false" class="ml-auto" />
    </div>

    <div class="campaign-card__actions">
      <b-button variant="primary" size="sm" @click="$emit('generate-link', participation)">
        <feather-icon icon="Link2Icon" size="14" class="mr-50" />
        {{ $t('influencer_panel.generate_link') }}
      </b-button>
      <b-button variant="outline-secondary" size="sm" @click="$emit('material', participation)">
        <feather-icon icon="ImageIcon" size="14" class="mr-50" />
        {{ $t('influencer_panel.material') }}
      </b-button>
      <b-button variant="outline-secondary" size="sm" @click="$emit('info', participation)">
        <feather-icon icon="InfoIcon" size="14" class="mr-50" />
        {{ $t('influencer_panel.info_button') }}
      </b-button>
      <b-button
        v-if="participation.status === 'AC'"
        variant="flat-danger"
        size="sm"
        class="ml-auto"
        @click="$emit('leave', participation)"
      >
        {{ $t('influencer_panel.leave') }}
      </b-button>
    </div>
  </hb-section>
</template>

<script>
import { BBadge, BButton } from 'bootstrap-vue'
import HbSection from '@/components/hubee/HbSection.vue'
import HbMoney from '@/components/hubee/HbMoney.vue'
import HbStatusBadge from '@/components/hubee/HbStatusBadge.vue'
import HbCopyButton from '@/components/hubee/HbCopyButton.vue'
import { campaignName, entityLine, commissionLabel, participationMetric, discountLabel } from '../campaign'
import { shortDate } from '../panel'

/** Campanha aderida, em detalhe: quanto rendeu, quanto converteu e quanto falta acabar. */
export default {
  name: 'CampaignCard',
  components: {
    BBadge, BButton, HbSection, HbMoney, HbStatusBadge, HbCopyButton,
  },
  props: {
    participation: { type: Object, required: true },
  },
  computed: {
    endingSoon() {
      const days = this.participation.days_to_end

      return this.participation.status === 'AC' && days !== null && days !== undefined
        && days >= 0 && days <= 7
    },
    conversionRate() {
      const value = participationMetric(this.participation, 'conversion_rate')

      return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`
    },
    periodRange() {
      const { starts_at: start, ends_at: end } = this.participation

      return `${shortDate(start)} a ${shortDate(end)}`
    },
    couponDiscount() {
      return discountLabel(this.participation.campaign)
    },
  },
  methods: {
    campaignName,
    entityLine,
    commissionLabel,
    metric(key) {
      return participationMetric(this.participation, key)
    },
  },
}
</script>

<style scoped>
.campaign-card__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.campaign-card__identity {
  min-width: 0;
}

.campaign-card__name {
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.campaign-card__entity {
  font-size: var(--fs-sm);
  color: var(--text-muted);
}

.campaign-card__earned {
  margin-top: 1rem;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.campaign-card__earned-label {
  font-size: var(--fs-sm);
  color: var(--text-muted);
}

.campaign-card__metrics {
  margin-top: 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(84px, 1fr));
  gap: 0.75rem;
}

.campaign-card__metric-label {
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.campaign-card__metric-value {
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.campaign-card__period {
  margin-top: 1rem;
}

.campaign-card__period-row {
  display: flex;
  justify-content: space-between;
  font-size: var(--fs-xs);
  color: var(--text-muted);
  margin-bottom: 0.35rem;
}

.campaign-card__bar {
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--surface-sunken);
  overflow: hidden;
}

.campaign-card__bar-fill {
  height: 100%;
  background: var(--primary);
  border-radius: var(--radius-pill);
}

.campaign-card__coupon {
  margin-top: 1.1rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  background: var(--surface-sunken);
  border-radius: var(--radius);
  color: var(--text-muted);
}

.campaign-card__coupon-code {
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.campaign-card__coupon-discount {
  font-size: var(--fs-xs);
}

.campaign-card__actions {
  margin-top: 1.1rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
</style>
